import React, { useEffect, useState } from 'react';
import axios from "axios";
import { Box, Button, VStack, Text, Input, HStack, Spinner,Card,CardBody,CardHeader,Heading } from "@chakra-ui/react";
import { FaInstagram } from "react-icons/fa";
import { useParams } from "react-router-dom";
import {
    initializeFacebookSDK,
    facebookLogin,
    obtainLongLivedToken,
    getPageId,
    getPageIdFromInstagramId
} from "@/utils/instagram/services/facebookService";
import {
    saveAuthData,
    getDjangoToken
} from "@/utils/instagram/services/apiService";
import { customAuthAxios } from "@/utils/customAuthAxios";

const InstagramSetting: React.FC = () => {
    const { restaurantId } = useParams<{ restaurantId: string }>();
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [businessAccountId, setBusinessAccountId] = useState<string | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true); // ローディング状態を管理
    const [isEditingId, setIsEditingId] = useState(false);
    const [newBusinessAccountId, setNewBusinessAccountId] = useState<string>("");
    const [newPageId, setNewPageId] = useState<string>("");
    const [pageId, setPageId] = useState<string | null>(null);

    useEffect(() => {
        const checkAuthStatus = async () => {
            try {
                const djangoToken = await getDjangoToken();
                if (!djangoToken) {
                    console.warn("Django token not available");
                    return;
                }

                const response = await customAuthAxios.get(
                    `external_api/instagram-auth/${restaurantId}/`,
                    { headers: { Authorization: `Bearer ${djangoToken}` } }
                );

                const { token, instagram_id, page_id } = response.data;
                setAccessToken(token);
                setBusinessAccountId(instagram_id);
                setPageId(page_id);
                setIsAuthenticated(true);
            } catch (error) {
                if (axios.isAxiosError(error)) {
                    if (error.response?.status === 404) {
                        // console.log("No auth data found. Prompting user to login.");
                    } else {
                        // console.error("Error checking auth status:", error);
                    }
                }
            } finally {
                setIsLoading(false); // ローディング終了
            }
        };

        checkAuthStatus();
    }, [restaurantId]);

    const getFrontendUrl = () => {
        const env = process.env.REACT_APP_ENVIRONMENT;
        console.log('REACT_APP_ENVIRONMENT:', env);
        console.log('env全部:', process.env);
        switch (env) {
            case 'local':
                return process.env.REACT_APP_BLOCKS_FRONTEND_LOCAL_URL;
            case 'stg':
                return process.env.REACT_APP_BLOCKS_FRONTEND_STG_URL;
            case 'prod':
                return process.env.REACT_APP_BLOCKS_FRONTEND_PROD_URL;
            default:
                return process.env.REACT_APP_BLOCKS_FRONTEND_LOCAL_URL;
        }
    };

    const handleInstagramLogin = () => {
        const clientId = '1087261756291854';
        const frontendUrl = getFrontendUrl();
        const redirectUri = `${frontendUrl}/restaurants/instagram/instagramAuth/callback`; // ✅ 完全固定
        const state = encodeURIComponent(restaurantId || ''); // ✅ restaurantIdはstateで持ち回る

        const scope = [
            'instagram_business_basic',
            'instagram_business_manage_messages',
            'instagram_business_manage_comments',
            'instagram_business_content_publish'
        ].join(',');

        const responseType = 'code';

        const instagramAuthUrl = `https://www.instagram.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=${responseType}&state=${state}`;

        console.log('Instagram Business Login URL:', instagramAuthUrl);

        window.location.href = instagramAuthUrl;
    };

    const handleLogin = async () => {
        try {
            await initializeFacebookSDK();
            const shortLivedToken = await facebookLogin();
            if (shortLivedToken) {
                const longLivedToken = await obtainLongLivedToken(shortLivedToken);
                if (longLivedToken) {
                    setAccessToken(longLivedToken);

                    const djangoToken = await getDjangoToken();
                    const pageId = await getPageIdFromInstagramId(businessAccountId!, longLivedToken);

                    if (djangoToken && restaurantId && businessAccountId && pageId) {
                        await saveAuthData(
                            restaurantId,
                            longLivedToken,
                            businessAccountId,
                            djangoToken,
                            pageId
                        );
                    } else {
                        console.warn("Missing required values to save auth data.");
                    }

                    setIsAuthenticated(true);
                } else {
                    // console.log("Failed to obtain long-lived token.");
                }
            } else {
                // console.log("Facebook login failed or token retrieval failed.");
            }
        } catch (error) {
            console.error("Error during Facebook login:", error);
        }
    };



    const handleSaveNewId = async () => {
        try {
            const djangoToken = await getDjangoToken();
            if (!djangoToken || !newBusinessAccountId || !accessToken) return;

            let finalPageId: string | null = newPageId;

            // newPageId が未入力なら自動取得を試みる
            if (!newPageId) {
                finalPageId = await getPageIdFromInstagramId(newBusinessAccountId, accessToken);
                if (!finalPageId) {
                    console.warn("ページIDの取得に失敗しました");
                    return;
                }
            }

            await customAuthAxios.post(
                `external_api/instagram-auth/${restaurantId}/`,
                {
                    instagram_id: newBusinessAccountId,
                    token: accessToken,
                    page_id: finalPageId,
                },
                { headers: { Authorization: `Bearer ${djangoToken}` } }
            );

            setBusinessAccountId(newBusinessAccountId);
            setPageId(finalPageId); // 状態更新
            setNewBusinessAccountId("");
            setNewPageId("");
            setIsEditingId(false);
        } catch (error) {
            console.error("Error saving new Instagram/Page ID:", error);
        }
    };

    return (
        <Box>
            {/* ページタイトル */}
            <HStack spacing={2} mb={4}>
                <FaInstagram size={24} />
                <Text fontSize="2xl" fontWeight="bold">Instagram連携設定</Text>
            </HStack>

            {isLoading ? (
                // ローディング中のスピナーを表示
                <VStack spacing={4} p={4} w="full">
                    <Spinner size="xl" />
                    <Text>読み込み中...</Text>
                </VStack>
            ) : (
                <VStack spacing={4} p={4} w="full" align="start"> {/* 左揃え */}
                    {!isAuthenticated ? (
                        <>
                            {/* 認証前の説明とログインボタン */}
                            <Heading size="md">Instagramアカウントの連携</Heading> {/* 見出しを追加 */}
                            <Text>Instagramアカウントを連携するには、Facebookでログインしてください。</Text>
                            <Button colorScheme="teal" onClick={handleLogin}>
                                Facebookでログイン
                            </Button>

                            {/*<Button colorScheme="pink" onClick={handleInstagramLogin}>*/}
                            {/*    Instagramでログイン*/}
                            {/*</Button>*/}
                        </>
                    ) : (
                        <>
                            {/* 現在のInstagram ID確認セクション */}
                            <Heading size="md">現在のInstagram ID</Heading> {/* 見出しを追加 */}
                            <Text>現在設定されているInstagramビジネスIDは下記のIDです。<br></br>正常に表示されない場合は再度連携をしてください。</Text>
                            <Box>
                                <Text fontSize="lg" fontWeight="bold">
                                    Instagram ID: {businessAccountId || "未設定"}
                                </Text>
                                <Text fontSize="lg" fontWeight="bold" mt={1}>
                                    Page ID: {pageId || "未設定"}
                                </Text>
                                {businessAccountId ? (
                                    !isEditingId ? (
                                        <Button mt={2} colorScheme="blue" onClick={() => setIsEditingId(true)}>
                                            Instagram ID・Facebook Page IDを変更
                                        </Button>
                                    ) : (
                                        <>
                                            <Input
                                                placeholder="新しいInstagram IDを入力"
                                                value={newBusinessAccountId}
                                                onChange={(e) => setNewBusinessAccountId(e.target.value)}
                                            />
                                            <Input
                                                placeholder="新しいPage IDを入力"
                                                mt={2}
                                                value={newPageId}
                                                onChange={(e) => setNewPageId(e.target.value)}
                                            />
                                            <Button mt={2} mr={2} colorScheme="teal" onClick={handleSaveNewId} disabled={!newBusinessAccountId}>
                                                保存
                                            </Button>
                                            <Button mt={2} colorScheme="red" onClick={() => setIsEditingId(false)}>
                                                キャンセル
                                            </Button>
                                        </>
                                    )
                                ) : (
                                    <>
                                        <Input
                                            placeholder="InstagramビジネスアカウントIDを入力"
                                            value={newBusinessAccountId}
                                            onChange={(e) => setNewBusinessAccountId(e.target.value)}
                                        />
                                        {/*<Input*/}
                                        {/*    placeholder="新しいPage IDを入力"*/}
                                        {/*    mt={2}*/}
                                        {/*    value={newPageId}*/}
                                        {/*    onChange={(e) => setNewPageId(e.target.value)}*/}
                                        {/*/>*/}
                                        {/*<Button*/}
                                        {/*    mt={2}*/}
                                        {/*    colorScheme="teal"*/}
                                        {/*    onClick={handleSaveNewId}*/}
                                        {/*    disabled={!newBusinessAccountId || !newPageId}*/}
                                        {/*>*/}
                                        {/*    Instagram IDを保存*/}
                                        {/*</Button>*/}
                                    </>
                                )}
                            </Box>

                            {/* Facebook連携設定変更セクション */}
                            <Box mt={6} w="full">
                                <Heading size="md">Facebook連携の再設定</Heading>
                                <Text mt={2}>
                                    Facebookの連携設定自体を変更する場合はこちらをご利用ください。
                                </Text>
                                <Button mt={2} colorScheme="teal" onClick={handleLogin}>
                                    Facebook連携を再設定
                                </Button>
                            </Box>

                            {/*<Box mt={6} w="full">*/}
                            {/*    <Heading size="md">Instagram連携の再設定</Heading>*/}
                            {/*    <Text mt={2}>*/}
                            {/*        Instagramの連携設定を変更する場合はこちらをご利用ください。*/}
                            {/*    </Text>*/}
                            {/*    <Button mt={2} colorScheme="pink" onClick={handleInstagramLogin}>*/}
                            {/*        Instagram連携を再設定*/}
                            {/*    </Button>*/}
                            {/*</Box>*/}
                        </>
                    )}
                </VStack>
            )}
        </Box>
    );
};

export default InstagramSetting;